/**
 * Safari extension auth via same-tab navigation directly through the OAuth
 * provider, with a hosted relay page as the https return leg.
 *
 * Safari extensions have no identity.launchWebAuthFlow, popups are blocked in
 * extension pages, and OAuth providers can only redirect to https URLs (never
 * to safari-web-extension:// pages) — so an https landing page is unavoidable.
 * Everything else is direct and stateless (iOS Safari partitions/clears the
 * storage Firebase's own popup/redirect flows depend on).
 *
 * Flow:
 *   1. signInViaSafariRelay() navigates the tab STRAIGHT to Google/Apple OAuth;
 *      returnUrl + provider (+ Apple rawNonce) ride in the OAuth `state` param
 *   2. The provider redirects back to the hosted relay page with the token in
 *      the URL fragment; the relay completes signInWithCredential and
 *      immediately redirects back to the extension page with ?relayToken=...
 *   3. index.tsx's captureRelayToken() runs on load and stores the token in ext.storage.local
 *   4. AuthContext.initRelayAuth() picks it up, exchanges for a custom token, and signs in
 *
 * The hosted upgrade page (account.rhemn.com/upgrade) reuses the same flow: on
 * the plain web there is no identity API and popup OAuth is dev-only, so Google
 * and Apple go through the relay too. The relay allowlists exactly
 * ACCOUNT_UPGRADE_URL; captureRelayToken() stores the result in sessionStorage
 * there, and UpgradePage consumes it via consumeWebRelayResult().
 *
 * Two web-only safeguards: the relay returns the token in the URL FRAGMENT
 * (never sent to the server, so never in hosting/CDN logs), and each attempt
 * carries a random verifier, kept in this tab's sessionStorage and sent
 * through the OAuth state. A result whose verifier doesn't match the attempt
 * this browser started is refused, so a replayed callback link can't sign a
 * victim in to someone else's account.
 */

import {
  signInWithCustomToken,
  setPersistence,
  indexedDBLocalPersistence,
} from 'firebase/auth/web-extension';
import { auth } from '../config/firebaseConfig';
import { ext, isExtensionPage } from '../utils/browserApi';
import { log, error as logError } from '../utils/logger';

export interface RelayAuthResult {
  user: {
    uid: string;
    displayName: string | null;
    email: string | null;
    photoURL: string | null;
    providerId: string;
  } | null;
  error?: string;
}

export const RELAY_STORAGE_KEY = 'auth:relay:result';
/** Per-attempt verifier for the web relay leg (sessionStorage, this tab only). */
export const RELAY_VERIFIER_KEY = 'auth:relay:verifier';
const RELAY_URL = 'https://rehmn-48777.firebaseapp.com/auth-relay.html';

/** The one web return URL hosting/auth-relay.html accepts (exact match). */
export const ACCOUNT_UPGRADE_URL = 'https://account.rhemn.com/upgrade';

/** True on the hosted upgrade page — the only web page the relay returns to. */
export const isAccountUpgradePage = (): boolean => {
  if (typeof window === 'undefined') return false;
  return `${window.location.origin}${window.location.pathname}` === ACCOUNT_UPGRADE_URL;
};

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const APPLE_SERVICE_ID = process.env.REACT_APP_APPLE_SERVICE_ID || '';

// URL-safe base64 — must match b64urlDecode in auth-relay.html.
const b64urlEncode = (obj: unknown): string =>
  btoa(JSON.stringify(obj)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const randomHex = (bytes: number): string => {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
};

const sha256Hex = async (str: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Navigate the current tab straight to the OAuth provider; the hosted relay
 * page handles only the return leg (token in the URL fragment → Firebase
 * signInWithCredential → redirect back here with ?relayToken=...).
 * The page navigates away — this promise intentionally never resolves
 * (the page is replaced). Auth result is handled by AuthContext on the
 * next page load via initRelayAuth().
 */
export function signInViaSafariRelay(
  provider: 'google' | 'apple',
  /** Defaults to the extension's index.html; the web upgrade page passes ACCOUNT_UPGRADE_URL. */
  returnUrlOverride?: string,
): Promise<RelayAuthResult> {
  void (async () => {
    const returnUrl = returnUrlOverride ?? ((ext as any)?.runtime?.getURL?.('index.html') || '');
    const clientId = provider === 'apple' ? APPLE_SERVICE_ID : GOOGLE_CLIENT_ID;

    // Web return only: bind the result to this attempt (see header comment).
    let verifier = '';
    if (returnUrlOverride) {
      verifier = randomHex(32);
      try {
        sessionStorage.setItem(RELAY_VERIFIER_KEY, verifier);
      } catch {
        logError('[SafariRelay] sessionStorage unavailable; cannot start web sign-in');
        return;
      }
    }

    if (!clientId) {
      // Client id not baked into this build — fall back to the relay page's
      // own sign-in button flow.
      const relayUrl = `${RELAY_URL}?provider=${provider}&returnUrl=${encodeURIComponent(returnUrl)}`
        + (verifier ? `&verifier=${verifier}` : '');
      log('[SafariRelay] No client id in build; navigating to relay:', relayUrl);
      window.location.href = relayUrl;
      return;
    }

    const statePayload: Record<string, string> = { returnUrl, provider };
    if (verifier) statePayload.verifier = verifier;
    let url: URL;
    if (provider === 'apple') {
      const rawNonce = randomHex(16);
      statePayload.rawNonce = rawNonce;
      url = new URL('https://appleid.apple.com/auth/authorize');
      url.searchParams.set('client_id', APPLE_SERVICE_ID);
      url.searchParams.set('redirect_uri', RELAY_URL);
      url.searchParams.set('response_type', 'code id_token');
      url.searchParams.set('response_mode', 'fragment');
      url.searchParams.set('nonce', await sha256Hex(rawNonce));
    } else {
      url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      url.searchParams.set('client_id', GOOGLE_CLIENT_ID);
      url.searchParams.set('redirect_uri', RELAY_URL);
      url.searchParams.set('response_type', 'token');
      url.searchParams.set('scope', 'email profile');
      url.searchParams.set('prompt', 'select_account');
    }
    url.searchParams.set('state', b64urlEncode(statePayload));

    log('[SafariRelay] Navigating directly to provider OAuth:', url.origin);
    window.location.href = url.toString();
  })();

  // Page is navigating away — this promise is never resolved (intentional).
  return new Promise(() => {});
}

// Derive backend base URL from the GraphQL URL env var (strips the /graphql path).
const BACKEND_BASE_URL = (() => {
  const fallback = process.env.NODE_ENV === 'development' ? 'http://localhost:8090' : '';
  try {
    const graphqlUrl = process.env.REACT_APP_GRAPHQL_API_URL || '';
    return graphqlUrl ? new URL(graphqlUrl).origin : fallback;
  } catch {
    return fallback;
  }
})();

/**
 * Detect a relay result deposited by the hosted auth relay page, persist it,
 * then strip it from the URL. Extension pages get `?relayToken=` and write to
 * ext.storage.local (AuthContext.initRelayAuth reads it). The hosted web page
 * gets `#relayToken=` (fragment: never sent to a server) and writes to
 * sessionStorage (UpgradePage reads it via consumeWebRelayResult). Must run
 * before React renders AND before Sentry init, so no telemetry sees the URL.
 */
export function captureRelayToken(): void {
  try {
    const extensionPage = isExtensionPage() && !!ext?.storage?.local?.set;
    const source = extensionPage ? window.location.search : window.location.hash.replace(/^#/, '?');
    const raw = new URLSearchParams(source).get('relayToken');
    if (!raw) return;
    window.history.replaceState({}, '', window.location.pathname);
    const data = JSON.parse(decodeURIComponent(raw));
    if (extensionPage) {
      ext.storage.local.set({ [RELAY_STORAGE_KEY]: data });
    } else {
      sessionStorage.setItem(RELAY_STORAGE_KEY, JSON.stringify(data));
    }
  } catch {
    // Non-relay page loads land here silently — ignore.
  }
}

/** Hard cap on the backend exchange. An abort (unlike a give-up timer) cancels
 *  the request, so a slow exchange can never sign anyone in after the fact. */
export const RELAY_EXCHANGE_TIMEOUT_MS = 12_000;

/** Exchange a relay ID token for a Firebase custom token and sign in. */
async function exchangeRelayIdToken(idToken: string): Promise<RelayAuthResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), RELAY_EXCHANGE_TIMEOUT_MS);
  try {
    log('[SafariRelay] Exchanging ID token for custom token...');
    if (!BACKEND_BASE_URL) throw new Error('Relay auth requires a configured backend URL');
    let customToken: unknown;
    try {
      const res = await fetch(`${BACKEND_BASE_URL}/auth/custom-token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
        signal: controller.signal,
      });

      if (!res.ok) {
        const body: unknown = await res.json().catch(() => ({}));
        const bodyError = (body as { error?: unknown })?.error;
        const msg = typeof bodyError === 'string' ? bodyError : `Custom token exchange failed (${res.status})`;
        logError('[SafariRelay] Token exchange failed:', msg);
        return { user: null, error: msg };
      }

      ({ customToken } = await res.json());
    } catch (e) {
      if (controller.signal.aborted) {
        logError('[SafariRelay] Token exchange timed out');
        return { user: null, error: 'Sign-in timed out. Please try again.' };
      }
      throw e;
    } finally {
      clearTimeout(timer);
    }
    if (typeof customToken !== 'string' || !customToken) throw new Error('Malformed custom token response');

    await setPersistence(auth, indexedDBLocalPersistence);
    const firebaseResult = await signInWithCustomToken(auth, customToken);

    log('[SafariRelay] Sign-in successful:', firebaseResult.user.uid);
    return {
      user: {
        uid: firebaseResult.user.uid,
        displayName: firebaseResult.user.displayName,
        email: firebaseResult.user.email,
        photoURL: firebaseResult.user.photoURL,
        providerId: firebaseResult.user.providerData[0]?.providerId || 'unknown',
      },
    };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    logError('[SafariRelay] relay exchange error:', msg);
    return { user: null, error: msg };
  }
}

/** True when a relay result is waiting for the hosted page (non-destructive). */
export function hasPendingWebRelayResult(): boolean {
  try {
    return sessionStorage.getItem(RELAY_STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Web counterpart of initRelayAuth for the hosted upgrade page. Returns null
 * when no relay result is pending (the normal page load); otherwise consumes
 * it (never replayed) and signs in.
 */
export async function consumeWebRelayResult(): Promise<RelayAuthResult | null> {
  let raw: string | null = null;
  let expectedVerifier: string | null = null;
  try {
    raw = sessionStorage.getItem(RELAY_STORAGE_KEY);
    sessionStorage.removeItem(RELAY_STORAGE_KEY);
    expectedVerifier = sessionStorage.getItem(RELAY_VERIFIER_KEY);
    // One attempt, one result: the verifier is spent whatever happens next.
    if (raw) sessionStorage.removeItem(RELAY_VERIFIER_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let stored: { idToken?: unknown; error?: unknown; verifier?: unknown };
  try {
    stored = JSON.parse(raw) as { idToken?: unknown; error?: unknown; verifier?: unknown };
  } catch {
    return { user: null, error: 'Malformed relay result' };
  }
  // Only a result for the attempt THIS tab started is accepted.
  if (!expectedVerifier || stored?.verifier !== expectedVerifier) {
    logError('[SafariRelay] Web relay result rejected: verifier mismatch');
    return { user: null, error: 'Sign-in could not be verified. Please try again.' };
  }
  if (typeof stored?.idToken !== 'string' || !stored.idToken) {
    return { user: null, error: typeof stored?.error === 'string' ? stored.error : 'Sign-in did not complete' };
  }
  return exchangeRelayIdToken(stored.idToken);
}

/**
 * Exchange a relay-stored ID token for a Firebase custom token and sign in.
 * Called on every app init — no-op when no relay result is pending in storage.
 */
export async function initRelayAuth(): Promise<RelayAuthResult> {
  // Read the relay result stored by detectSafariRelayToken() in App.tsx
  const stored = await new Promise<{ idToken?: string; error?: string } | null>((resolve) => {
    if (!(ext as any)?.storage?.local?.get) {
      resolve(null);
      return;
    }
    ext.storage.local.get([RELAY_STORAGE_KEY], (res: Record<string, any>) => {
      if (ext.runtime?.lastError) { resolve(null); return; }
      resolve(res?.[RELAY_STORAGE_KEY] || null);
    });
  });

  if (!stored?.idToken) return { user: null };

  // Consume the stored result immediately so it isn't replayed on the next load
  try { ext.storage.local.remove([RELAY_STORAGE_KEY]); } catch { /* ignore */ }

  return exchangeRelayIdToken(stored.idToken);
}
